/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import _toNumber from "lodash/toNumber";
import _slice from "lodash/slice";
import _cloneDeep from "lodash/cloneDeep";
import { Box, MenuItem } from "@mui/material";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { requiredValidator } from "@/src/services/validationRules";
import PensionInsurance from "./PensionInsurance/PensionInsurance";
import Typography from "@mui/material/Typography";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";

type CashValuablesProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function CashValuables({ submitProps, product }: CashValuablesProps) {
  const { values, handleChange, handleSubmit } = useForm();
  useEffect(() => {
    const numberofPensionInsurances = values.numberofPensionInsurances ? _toNumber(values.numberofPensionInsurances) : 0;
    if (values.pensionInsurances.length === numberofPensionInsurances) {
      return;
    }
    const emptyPensionInsurance = {
      name: "",
      policyHolder: "",
      date: "",
      value: "",
    };
    let pensionInsurances = _cloneDeep(values.pensionInsurances);
    for (let i = pensionInsurances.length; i < Math.min(numberofPensionInsurances, 5); i++) {
      pensionInsurances.push(_cloneDeep(emptyPensionInsurance));
    }
    pensionInsurances = _slice(pensionInsurances, 0, numberofPensionInsurances);
    handleChange({
      target: {
        type: "other",
        name: "pensionInsurances",
        value: pensionInsurances,
      },
    });
  }, [values.pensionInsurances, values.numberofPensionInsurances]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vermögen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasPensionInsurance"}
                question={"Haben Sie eine Lebensversicherung oder private Rentenversicherung (z.B. Riester-Rente)?"}
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasPensionInsurance === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie viele Lebensversicherungen besitzen Sie?"
                  name="numberofPensionInsurances"
                  validators={[requiredValidator("Bitte geben Sie an wie viele Lebensversicherungen Sie besitzen")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.pensionInsurances?.map((vehicle: any, index: number) => (
                  <PensionInsurance key={index} index={index} path={`pensionInsurances[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
